'use client';

import React from 'react';
import { useHandRaise } from './HandRaiseContext';
import { Hand } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';

const BADGE_ATTR = 'data-raise-badge';
const TILE_SELECTORS =
  '.lk-participant-tile, .lk-grid-item, .lk-stage-participant, .lk-participant';
const NAME_SELECTORS = [
  '.lk-participant-name',
  '[class*="participant-name"]',
  '[data-participant-name]',
];

const DEBUG =
  typeof process !== 'undefined' && process.env.NEXT_PUBLIC_DEBUG_HAND_RAISE === '1';

function getTileName(tile: HTMLElement): string | null {
  for (const sel of NAME_SELECTORS) {
    const el = tile.querySelector<HTMLElement>(sel);
    if (el?.textContent) return el.textContent.trim();
  }
  return null;
}

function getTileIdentity(tile: HTMLElement): string | null {
  return (
    tile.getAttribute('data-lk-participant-identity') ||
    tile.getAttribute('data-participant-id') ||
    tile.getAttribute('data-identity') ||
    tile.getAttribute('data-lk-identity')
  );
}

function isOurMutation(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  return el.hasAttribute?.(BADGE_ATTR) || !!el.closest?.(`[${BADGE_ATTR}]`);
}

/**
 * Destaca tiles com mão levantada.
 * Importante: NÃO observar document.body sem debounce — isso criava loop com
 * re-renders do LiveKit + logs contínuos e pressão na main thread.
 */
export function HighlightRaisedTiles() {
  const { handRaisedParticipants } = useHandRaise();
  const lastSignatureRef = React.useRef<string>('');
  const scheduledRef = React.useRef<number | null>(null);

  const applyHighlights = React.useCallback(() => {
    const raised = Array.from(handRaisedParticipants);
    const baseNames = new Set(raised.map((id) => (id || '').split('__')[0].trim()).filter(Boolean));
    const identities = new Set(raised);

    const tiles = Array.from(document.querySelectorAll<HTMLElement>(TILE_SELECTORS));

    // Evita trabalho/DOM writes quando nada mudou (raised set + tiles count + identities no DOM).
    const tileKeys = tiles
      .map((t) => `${getTileIdentity(t) ?? ''}|${getTileName(t) ?? ''}`)
      .join(';');
    const signature = `${raised.slice().sort().join(',')}|${tiles.length}|${tileKeys}`;
    if (signature === lastSignatureRef.current) {
      return;
    }
    lastSignatureRef.current = signature;

    if (DEBUG) {
      console.debug('[HighlightRaisedTiles]', {
        raised,
        tiles: tiles.length,
      });
    }

    tiles.forEach((tile) => {
      const name = getTileName(tile);
      const identity = getTileIdentity(tile);
      const shouldHighlight = !!(
        (name && baseNames.has(name)) ||
        (identity && identities.has(identity))
      );

      const hasHighlight = tile.classList.contains('raised-hand-highlight');
      let badge = tile.querySelector<HTMLElement>(`[${BADGE_ATTR}]`);

      if (shouldHighlight === hasHighlight && !!badge === shouldHighlight) {
        return;
      }

      if (shouldHighlight) {
        if (tile.style.position !== 'relative' && getComputedStyle(tile).position === 'static') {
          tile.style.position = 'relative';
        }
        tile.classList.add('raised-hand-highlight');
        tile.style.border = '4px solid #ffd700';
        tile.style.borderRadius = '10px';
        tile.style.boxSizing = 'border-box';

        if (!badge) {
          badge = document.createElement('div');
          badge.setAttribute(BADGE_ATTR, 'true');
          badge.className = 'raised-hand-tile-badge';
          badge.style.cssText = `
            position: absolute;
            top: 8px;
            left: 8px;
            width: 32px;
            height: 32px;
            background: #ffd700;
            color: #000000;
            border-radius: 6px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: 700;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
            z-index: 15;
            pointer-events: none;
          `;
          badge.innerHTML = renderToStaticMarkup(
            <Hand size={20} strokeWidth={2} color="#000" />,
          );
          tile.appendChild(badge);
        }
      } else {
        tile.classList.remove('raised-hand-highlight');
        if (tile.style.border) tile.style.border = '';
        if (tile.style.borderRadius) tile.style.borderRadius = '';
        if (tile.style.boxSizing) tile.style.boxSizing = '';
        if (badge?.parentElement) {
          badge.parentElement.removeChild(badge);
        }
      }
    });
  }, [handRaisedParticipants]);

  const scheduleApply = React.useCallback(() => {
    if (scheduledRef.current != null) return;
    scheduledRef.current = window.requestAnimationFrame(() => {
      scheduledRef.current = null;
      applyHighlights();
    });
  }, [applyHighlights]);

  React.useEffect(() => {
    lastSignatureRef.current = '';
    applyHighlights();
  }, [applyHighlights]);

  React.useEffect(() => {
    const root =
      document.querySelector('.lk-video-conference') ||
      document.querySelector('.lk-room-container') ||
      document.body;

    const obs = new MutationObserver((mutations) => {
      // Ignora mutações causadas pelo próprio badge — evita feedback loop.
      for (const m of mutations) {
        const nodes = [
          ...Array.from(m.addedNodes),
          ...Array.from(m.removedNodes),
          m.target,
        ];
        if (nodes.some((n) => n && isOurMutation(n))) {
          continue;
        }
        // Só reage a mudanças relevantes a tiles/participantes.
        const target = m.target as Element | null;
        if (
          target?.closest?.(TILE_SELECTORS) ||
          (target as Element)?.matches?.(TILE_SELECTORS) ||
          (target as Element)?.querySelector?.(TILE_SELECTORS)
        ) {
          scheduleApply();
          return;
        }
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          scheduleApply();
          return;
        }
      }
    });

    obs.observe(root, { childList: true, subtree: true });

    // Fallback raro (LiveKit às vezes troca atributo sem childList) — bem espaçado.
    const id = window.setInterval(() => {
      lastSignatureRef.current = '';
      applyHighlights();
    }, 8_000);

    return () => {
      obs.disconnect();
      window.clearInterval(id);
      if (scheduledRef.current != null) {
        window.cancelAnimationFrame(scheduledRef.current);
        scheduledRef.current = null;
      }
    };
  }, [applyHighlights, scheduleApply]);

  return null;
}
